/* ─── Base ─── */

.root {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  position: relative;
  box-sizing: border-box;
  border: none;
  font-family: var(--font-sans);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background var(--motion-duration-short3)
      var(--motion-easing-standard),
    box-shadow var(--motion-duration-short3) var(--motion-easing-standard),
    opacity var(--motion-duration-short3) var(--motion-easing-standard);
}

/* ─── State layer ─── */

.root::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .root:hover::before {
    opacity: var(--state-hover);
  }
}

.root:focus-visible::before {
  opacity: var(--state-focus);
}

.root:active::before {
  opacity: var(--state-pressed);
}

/* ─── Disabled ─── */

.root[aria-disabled="true"],
.root:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.root[aria-disabled="true"]::before,
.root:disabled::before {
  display: none;
}

/* ─── Sizes ─── */

.xs {
  font: var(--type-label-md);
  letter-spacing: var(--tracking-label-md);
  height: 2rem;
  padding-inline: var(--space-3);
  border-radius: var(--shape-full);
  gap: var(--space-1);
}

/* Keeps the 2.75rem touch target around the smaller visible button. */
.xs::after {
  content: "";
  position: absolute;
  inset-block: calc((2rem - 2.75rem) / 2);
  inset-inline: 0;
}

.sm {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  height: 2.75rem;
  padding-inline: var(--space-6);
  border-radius: var(--shape-xl);
}

.md {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  height: 3rem;
  padding-inline: var(--space-6);
  border-radius: var(--shape-xl);
}

.lg {
  font: var(--type-title-md);
  letter-spacing: var(--tracking-title-md);
  height: 3.5rem;
  padding-inline: var(--space-8);
  border-radius: var(--shape-xl);
}

/* ─── Variants ─── */

.filled {
  --focus-ring-color: var(--md-on-primary);
  background: var(--md-primary);
  color: var(--md-on-primary);
}

.tonal {
  background: var(--md-primary-container);
  color: var(--md-on-primary-container);
}

.outlined {
  background: transparent;
  color: var(--md-primary);
  border: 1px solid var(--md-outline);
}

.text {
  background: transparent;
  color: var(--md-primary);
  padding-inline: var(--space-3);
}

.elevated {
  background: var(--md-surface);
  color: var(--md-primary);
  box-shadow: var(--elevation-1);
}

@media (hover: hover) {
  .elevated:hover {
    box-shadow: var(--elevation-2);
  }
}

/* ─── Colors ─── */

/* color="primary" needs no rules — the variants above are already primary. Each
   danger rule pairs with its variant so emphasis and intent stay independent. */

.filled.color-error {
  --focus-ring-color: var(--md-on-error);
  background: var(--md-error);
  color: var(--md-on-error);
}

.tonal.color-error {
  background: var(--md-error-container);
  color: var(--md-on-error-container);
}

.outlined.color-error {
  color: var(--md-error);
  border-color: var(--md-error);
}

.text.color-error,
.elevated.color-error {
  color: var(--md-error);
}

.color-error:focus-visible {
  outline-color: var(--md-error);
}

.filled.color-success {
  background: var(--status-success);
  color: var(--md-on-primary);
}

.tonal.color-success {
  background: var(--status-success-container);
  color: var(--status-success);
}

.outlined.color-success {
  color: var(--status-success);
  border-color: var(--status-success);
}

.text.color-success,
.elevated.color-success {
  color: var(--status-success);
}

.color-success:focus-visible {
  outline-color: var(--status-success);
}

/* ─── Full width ─── */

.fullWidth {
  width: 100%;
}

/* ─── Icon slots ─── */

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon svg {
  width: 1em;
  height: 1em;
}

.xs .icon,
.sm .icon {
  font-size: var(--icon-size-xs);
}

.md .icon {
  font-size: var(--icon-size-sm);
}

.lg .icon {
  font-size: var(--icon-size-lg);
}

/* ─── Loading ─── */

.loading > :not(.spinner) {
  visibility: hidden;
}

.spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
